import { supabase } from "@/integrations/supabase/client";

type EventType = 'page_view' | 'view_content' | 'add_to_cart' | 'initiate_checkout' | 'purchase' | 'cart_abandonment';

interface TrackEventParams {
  event_type: EventType;
  product_id?: string;
  product_name?: string;
  quantity?: number;
  value?: number;
  page_path?: string;
}

// Simple session ID (persists per tab)
let sessionId: string | null = null;
function getSessionId() {
  if (!sessionId) {
    sessionId = sessionStorage.getItem('nlk_sid');
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      sessionStorage.setItem('nlk_sid', sessionId);
    }
  }
  return sessionId;
}

export async function trackEvent(params: TrackEventParams) {
  try {
    await supabase.from('analytics_events' as any).insert({
      event_type: params.event_type,
      product_id: params.product_id ?? null,
      product_name: params.product_name ?? null,
      quantity: params.quantity ?? null,
      value: params.value ?? null,
      currency: 'EUR',
      session_id: getSessionId(),
      page_path: params.page_path ?? window.location.pathname,
      user_agent: navigator.userAgent,
    });
  } catch {
    // Silent fail — analytics should never break the app
  }
}

export function trackPageViewEvent() {
  trackEvent({ event_type: 'page_view', page_path: window.location.pathname });
}

export function trackViewContentEvent(productId: string, productName: string, value: number) {
  trackEvent({ event_type: 'view_content', product_id: productId, product_name: productName, value });
}

export function trackAddToCartEvent(productId: string, productName: string, quantity: number, value: number) {
  trackEvent({ event_type: 'add_to_cart', product_id: productId, product_name: productName, quantity, value });
  // Start cart abandonment timer
  startCartAbandonmentTimer(productId, productName, quantity, value);
}

export function trackInitiateCheckoutEvent(value: number, numItems: number) {
  trackEvent({ event_type: 'initiate_checkout', value, quantity: numItems });
  // User proceeded to checkout — cancel abandonment
  cancelCartAbandonmentTimer();
}

export function trackPurchaseEvent(value: number, numItems: number) {
  trackEvent({ event_type: 'purchase', value, quantity: numItems });
  // Purchase completed — cancel abandonment
  cancelCartAbandonmentTimer();
}

// --- Cart abandonment detection (60 min) ---
const ABANDONMENT_DELAY_MS = 60 * 60 * 1000; // 60 minutes
let abandonmentTimer: ReturnType<typeof setTimeout> | null = null;
let lastCartProduct: { id: string; name: string; quantity: number; value: number } | null = null;

function startCartAbandonmentTimer(productId: string, productName: string, quantity: number, value: number) {
  // Reset any existing timer
  if (abandonmentTimer) clearTimeout(abandonmentTimer);

  lastCartProduct = { id: productId, name: productName, quantity, value };

  // Check if already abandoned this session (avoid duplicates)
  const abandonKey = `nlk_abandon_${getSessionId()}`;

  abandonmentTimer = setTimeout(() => {
    // Only fire if not already tracked this session
    if (!sessionStorage.getItem(abandonKey)) {
      trackEvent({
        event_type: 'cart_abandonment',
        product_id: lastCartProduct?.id,
        product_name: lastCartProduct?.name,
        quantity: lastCartProduct?.quantity,
        value: lastCartProduct?.value,
      });
      sessionStorage.setItem(abandonKey, '1');
    }
    abandonmentTimer = null;
  }, ABANDONMENT_DELAY_MS);
}

function cancelCartAbandonmentTimer() {
  if (abandonmentTimer) {
    clearTimeout(abandonmentTimer);
    abandonmentTimer = null;
  }
}
